// Custom field values (`FieldValue` in GroundTeam/Models/Record.swift) and `Record.customValues`.
//
// Wire form, from Swift's synthesized Codable (SE-0295: one key per case, unlabeled associated value under `_0`):
//   {"text":{"_0":"x"}}  {"number":{"_0":5}}  {"bool":{"_0":true}}  {"date":{"_0":"2026-09-29T14:03:11Z"}}
//   {"options":{"_0":["a","b"]}}
// `customValues` is `[UUID: FieldValue]`; JSONEncoder writes a dictionary with non-String keys as a flat array
// `["<UUID>", <FieldValue>, "<UUID>", <FieldValue>, …]` in hash order, and SyncReplica.canonical sorts the pairs by the
// key string so equal maps compare equal. The web writes that canonical form and never the `{}` object form (which an
// iPhone cannot decode). This file is the only place that builds or reads either shape.

import type { Json } from './wire';
import { isIosDecodableDate, isPlainObject, isWireDate } from './models/fields';
import { isUuid, sameId } from './uuid';
import { parseWireDate, toWireDate } from './wireDate';

/** Case names in Swift declaration order. */
export const FIELD_VALUE_KINDS = ['text', 'number', 'bool', 'date', 'options'] as const;
export type FieldValueKind = (typeof FIELD_VALUE_KINDS)[number];

/** A decoded custom field value. `date` keeps the payload string, so an untouched value is never reformatted. */
export type FieldValue =
  | { kind: 'text'; value: string }
  | { kind: 'number'; value: number }
  | { kind: 'bool'; value: boolean }
  | { kind: 'date'; value: string }
  | { kind: 'options'; value: string[] };

/** The JSON of one FieldValue. */
export type FieldValueJson =
  | { text: { _0: string } }
  | { number: { _0: number } }
  | { bool: { _0: boolean } }
  | { date: { _0: string } }
  | { options: { _0: string[] } };

/** `Record.customValues` on the wire: `[key, value, key, value, …]`. */
export type CustomValuesWire = (string | FieldValueJson)[];

// MARK: - Constructors

export const textValue = (value: string): FieldValue => ({ kind: 'text', value });
export const numberValue = (value: number): FieldValue => ({ kind: 'number', value });
export const boolValue = (value: boolean): FieldValue => ({ kind: 'bool', value });
/** A date value, formatted the only way payload dates may be (whole seconds, `Z`). */
export const dateValue = (value: Date): FieldValue => ({ kind: 'date', value: toWireDate(value) });
export const optionsValue = (value: readonly string[]): FieldValue => ({ kind: 'options', value: [...value] });

/** The Date of a `date` value (undefined for other kinds or an unparseable string). */
export function fieldValueDate(v: FieldValue): Date | undefined {
  return v.kind === 'date' ? parseWireDate(v.value) : undefined;
}

// MARK: - Decode / encode

function isFieldKind(key: string): key is FieldValueKind {
  return (FIELD_VALUE_KINDS as readonly string[]).includes(key);
}

/**
 * Reads one FieldValue the way Swift's synthesized decoder does, recording why iOS would reject it. Swift only counts
 * the keys that are case names (others are ignored), and needs exactly one.
 */
export function readFieldValue(raw: unknown, path: string, issues: string[]): FieldValue | undefined {
  if (!isPlainObject(raw)) {
    issues.push(`${path}: expected a FieldValue object`);
    return undefined;
  }
  const cases = Object.keys(raw).filter(isFieldKind);
  if (cases.length !== 1) {
    issues.push(`${path}: FieldValue needs exactly one of ${FIELD_VALUE_KINDS.join('/')}, got ${cases.length === 0 ? 'none' : cases.join('+')}`);
    return undefined;
  }
  const kind = cases[0] as FieldValueKind;
  const inner = raw[kind];
  if (!isPlainObject(inner) || !Object.prototype.hasOwnProperty.call(inner, '_0')) {
    issues.push(`${path}.${kind}: expected {"_0": …}`);
    return undefined;
  }
  const v = inner._0;
  const bad = (what: string): undefined => {
    issues.push(`${path}.${kind}._0: expected ${what}`);
    return undefined;
  };
  switch (kind) {
    case 'text':
      return typeof v === 'string' ? { kind, value: v } : bad('String');
    case 'number':
      return typeof v === 'number' && Number.isFinite(v) ? { kind, value: v } : bad('Double');
    case 'bool':
      return typeof v === 'boolean' ? { kind, value: v } : bad('Bool');
    case 'date':
      return isIosDecodableDate(v) ? { kind, value: v } : bad('an ISO 8601 date without fractional seconds');
    case 'options':
      return Array.isArray(v) && v.every((s) => typeof s === 'string') ? { kind, value: [...v] } : bad('[String]');
  }
}

/** Decodes one FieldValue; undefined when an iPhone could not read it. */
export function decodeFieldValue(raw: unknown): FieldValue | undefined {
  return readFieldValue(raw, 'value', []);
}

/** The wire JSON of a value. Throws RangeError for anything an iPhone could not decode (NaN, a `.sssZ` date, …). */
export function encodeFieldValue(v: FieldValue): FieldValueJson {
  switch (v.kind) {
    case 'text':
      if (typeof v.value !== 'string') throw new RangeError('FieldValue.text needs a string');
      return { text: { _0: v.value } };
    case 'number':
      if (typeof v.value !== 'number' || !Number.isFinite(v.value)) throw new RangeError('FieldValue.number needs a finite number');
      return { number: { _0: v.value === 0 ? 0 : v.value } };
    case 'bool':
      if (typeof v.value !== 'boolean') throw new RangeError('FieldValue.bool needs a boolean');
      return { bool: { _0: v.value } };
    case 'date':
      // Untouched server values may carry an offset iOS accepts; new ones come from dateValue (toWireDate).
      if (!isIosDecodableDate(v.value)) throw new RangeError(`FieldValue.date must be a whole-second ISO date, got "${String(v.value)}"`);
      return { date: { _0: v.value } };
    case 'options':
      if (!Array.isArray(v.value) || !v.value.every((s) => typeof s === 'string')) throw new RangeError('FieldValue.options needs strings');
      return { options: { _0: [...v.value] } };
    default:
      throw new RangeError(`Unknown FieldValue kind ${String((v as { kind?: unknown }).kind)}`);
  }
}

/**
 * `safeInt` (GroundTeam/Support/Numbers.swift): rounded and clamped to ±9e15, printed as plain digits (JS `String`
 * would switch to exponent notation above 1e21).
 */
function safeIntString(v: number): string {
  if (!Number.isFinite(v)) return '0';
  const limit = 9.0e15;
  return BigInt(Math.min(Math.max(Math.round(v), -limit), limit)).toString();
}

/** Swift's display string (`FieldValue.displayString`): integral numbers without decimals, else two places. */
export function fieldValueDisplay(v: FieldValue, locale?: string): string {
  switch (v.kind) {
    case 'text':
      return v.value;
    case 'number':
      return v.value === Math.round(v.value) ? safeIntString(v.value) : v.value.toFixed(2);
    case 'bool':
      return v.value ? 'Yes' : 'No';
    case 'date': {
      const d = parseWireDate(v.value);
      return d ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(d) : v.value;
    }
    case 'options':
      return v.value.join(', ');
  }
}

/** Structural equality of two values (dates compared as instants, so an offset and `Z` form of one time are equal). */
export function sameFieldValue(a: FieldValue, b: FieldValue): boolean {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case 'date':
      return parseWireDate(a.value)?.getTime() === parseWireDate(b.value as string)?.getTime();
    case 'options': {
      const bv = b.value as string[];
      return a.value.length === bv.length && a.value.every((s, i) => s === bv[i]);
    }
    default:
      return a.value === b.value;
  }
}

// MARK: - customValues

/**
 * Swift `String <` ordering for the canonical sort: Unicode scalar order of the NFC form. For the upper-case UUID keys
 * iOS writes this is plain ASCII order.
 */
export function compareSwiftStrings(a: string, b: string): number {
  const x = Array.from(a.normalize('NFC'), (c) => c.codePointAt(0) as number);
  const y = Array.from(b.normalize('NFC'), (c) => c.codePointAt(0) as number);
  const n = Math.min(x.length, y.length);
  for (let i = 0; i < n; i++) {
    if (x[i] !== y[i]) return (x[i] as number) - (y[i] as number);
  }
  return x.length - y.length;
}

/**
 * Port of `SyncReplica.canonical` for one flat UUID-keyed field: when the value is a non-empty, even-length array
 * whose even slots are all strings, the pairs are sorted by key; anything else is returned unchanged. Values are
 * never re-encoded, so untouched pairs stay byte-identical.
 */
export function canonicalCustomValues<T extends Json | undefined>(flat: T): T {
  if (!Array.isArray(flat) || flat.length === 0 || flat.length % 2 !== 0) return flat;
  const pairs: [string, Json, Json][] = [];
  for (let i = 0; i < flat.length; i += 2) {
    const key = flat[i];
    if (typeof key !== 'string') return flat;
    pairs.push([key, key, flat[i + 1] as Json]);
  }
  pairs.sort((p, q) => compareSwiftStrings(p[0], q[0]));
  return pairs.flatMap(([, k, v]) => [k, v]) as T;
}

/**
 * Reads `customValues` for a record decode. Returns the flat array exactly as received when iOS can decode it;
 * otherwise records why and returns the readable pairs (best effort, for read-only display).
 */
export function readCustomValues(raw: unknown, path: string, issues: string[]): CustomValuesWire | undefined {
  if (isPlainObject(raw)) {
    // `{}` / `{"<UUID>": …}`: what JSON.stringify of a map would give. Swift's [UUID: FieldValue] decoder wants the
    // flat array, so iOS cannot read this; show what we can.
    issues.push(`${path}: object form; iOS needs the flat ["<UUID>", FieldValue, …] array`);
    const flat: unknown[] = [];
    for (const [k, v] of Object.entries(raw)) flat.push(k, v);
    return readCustomValues(flat, path, []) ?? [];
  }
  if (!Array.isArray(raw)) {
    issues.push(`${path}: expected the flat ["<UUID>", FieldValue, …] array`);
    return undefined;
  }
  const before = issues.length;
  if (raw.length % 2 !== 0) issues.push(`${path}: odd number of elements (${raw.length}) in a key/value array`);
  const good: CustomValuesWire = [];
  for (let i = 0; i + 1 < raw.length; i += 2) {
    const key = raw[i];
    if (!isUuid(key)) {
      issues.push(`${path}[${i}]: expected a UUID key`);
      continue;
    }
    if (readFieldValue(raw[i + 1], `${path}[${i + 1}]`, issues) !== undefined) good.push(key, raw[i + 1] as FieldValueJson);
  }
  return issues.length === before ? (raw as CustomValuesWire) : good;
}

/**
 * The flat array as a map keyed by upper-case field id (Swift decodes the keys as UUIDs and re-encodes them upper-case).
 * Pairs an iPhone could not read are skipped; a repeated key keeps the last value, as Swift's decoder does.
 */
export function customValuesToMap(flat: unknown): Map<string, FieldValue> {
  const map = new Map<string, FieldValue>();
  if (!Array.isArray(flat)) return map;
  for (let i = 0; i + 1 < flat.length; i += 2) {
    const key = flat[i];
    if (!isUuid(key)) continue;
    const value = readFieldValue(flat[i + 1], 'value', []);
    if (value !== undefined) map.set(key.toUpperCase(), value);
  }
  return map;
}

/** The canonical flat array for a map (keys upper-cased, pairs sorted). Throws RangeError on a bad key or value. */
export function customValuesFromMap(map: ReadonlyMap<string, FieldValue>): CustomValuesWire {
  const seen = new Set<string>();
  const flat: CustomValuesWire = [];
  for (const [key, value] of map) {
    if (!isUuid(key)) throw new RangeError(`customValues key must be a UUID, got "${key}"`);
    const upper = key.toUpperCase();
    if (seen.has(upper)) throw new RangeError(`customValues has ${upper} twice`);
    seen.add(upper);
    flat.push(upper, encodeFieldValue(value));
  }
  return canonicalCustomValues(flat as Json[]) as CustomValuesWire;
}

/**
 * Sets (or with `undefined`, removes) one field's value in a flat array, as a patch should: the other pairs are kept
 * byte-for-byte (key casing, date strings), the edited key is written upper-case, and the result is canonical.
 */
export function withCustomValue(flat: unknown, fieldId: string, value: FieldValue | undefined): CustomValuesWire {
  if (!isUuid(fieldId)) throw new RangeError(`customValues key must be a UUID, got "${fieldId}"`);
  const source: unknown[] = Array.isArray(flat) ? flat : [];
  const out: Json[] = [];
  for (let i = 0; i + 1 < source.length; i += 2) {
    if (typeof source[i] === 'string' && sameId(source[i] as string, fieldId)) continue;
    out.push(source[i] as Json, source[i + 1] as Json);
  }
  if (value !== undefined) out.push(fieldId.toUpperCase(), encodeFieldValue(value) as unknown as Json);
  return canonicalCustomValues(out) as CustomValuesWire;
}

/** Checks a flat array for a new payload: valid pairs, no duplicate keys, keys upper-cased, canonical order. */
export function writeCustomValues(flat: unknown, path: string): CustomValuesWire {
  if (!Array.isArray(flat)) throw new RangeError(`${path}: expected the flat ["<UUID>", FieldValue, …] array (never {})`);
  if (flat.length % 2 !== 0) throw new RangeError(`${path}: odd number of elements`);
  const map = new Map<string, FieldValue>();
  for (let i = 0; i < flat.length; i += 2) {
    const key = flat[i];
    if (!isUuid(key)) throw new RangeError(`${path}[${i}]: expected a UUID key`);
    const issues: string[] = [];
    const value = readFieldValue(flat[i + 1], `${path}[${i + 1}]`, issues);
    if (value === undefined) throw new RangeError(issues[0] ?? `${path}[${i + 1}]: bad FieldValue`);
    if (value.kind === 'date' && !isWireDate(value.value)) {
      throw new RangeError(`${path}[${i + 1}]: new dates must be whole-second UTC (use dateValue/toWireDate)`);
    }
    const upper = key.toUpperCase();
    if (map.has(upper)) throw new RangeError(`${path}: ${upper} appears twice`);
    map.set(upper, value);
  }
  return customValuesFromMap(map);
}
